import React from 'react'
import styled from 'styled-components'
import { useEthers } from '../../hooks'
import { StoredTransaction, isOrder, isPending } from '../../providers/transactions/model'
import { ChainId, Colors, FontWeights } from '../../constants'
import { Spinner } from '../Spinner'
import { getEtherscanLinkForTransaction } from '../getEtherscanLink'
import { Item } from './items/Item'
import { ProgressBar } from './ProgressBar'

interface Props {
  className?: string
  tx: StoredTransaction
}

export function HistoryItem({ className, tx }: Props) {
  const { chainId = ChainId.Mainnet } = useEthers()
  const now = new Date()
  const [status, grayedOut] = getStatus(tx, now)

  return (
    <ListItem className={className}>
      <ListWrapper>
        <Item tx={tx} now={now} />
        <Footer>
          <Status grayedOut={grayedOut} warning={status === 'failed'}>
            {isPending(tx) && <SpinnerStyled />} {status}
          </Status>
          <TransactionHash>
            <Element>
              <strong>Tx:</strong>{' '}
            </Element>
            <Link href={getEtherscanLinkForTransaction(tx.hash, chainId)} target="_blank">
              <ShortenHash hash={tx.hash} />
            </Link>
            {tx.executionHash && (
              <>
                <Element>{', '}</Element>
                <Link href={getEtherscanLinkForTransaction(tx.executionHash, chainId)} target="_blank">
                  <ShortenHash hash={tx.executionHash} />
                </Link>
              </>
            )}
          </TransactionHash>
        </Footer>
        <ProgressBar tx={tx} now={now} />
      </ListWrapper>
    </ListItem>
  )
}

function getStatus(tx: StoredTransaction, now: Date): [string, boolean] {
  if (tx.droppedAndReplaced) {
    return ['dropped & replaced', false]
  } else if (!tx.receipt) {
    return ['mining...', true]
  } else if (isOrder(tx)) {
    if (tx.orderStatus && tx.orderStatus.status) {
      if (tx.orderStatus.status === 'enqueued') {
        const timestamp = new Date(tx.orderStatus.validAfterTimestamp)
        if (now >= timestamp) {
          return ['executing...', true]
        } else {
          return [
            `${tx.orderStatus.status} until ${timestamp.getHours().toString().padStart(2, '0')}:${timestamp
              .getMinutes()
              .toString()
              .padStart(2, '0')}:${timestamp.getSeconds().toString().padStart(2, '0')} ${
              timestamp.getHours() < 12 ? 'AM' : 'PM'
            }...`,
            true,
          ]
        }
      } else if (tx.orderStatus.status === 'reverted' && tx.orderStatus.errorMessage) {
        return [`${tx.orderStatus.status}: ${tx.orderStatus.errorMessage}`, false]
      }
    } else {
      return ['enqueueing...', true]
    }
  }
  return ['executed', false]
}

const ListItem = styled.li``

const Link = styled.a`
  text-decoration: none;
  color: ${Colors.Black};

  &:hover {
    color: ${Colors.Hover};
  }
`

const ListWrapper = styled.div`
  padding: 15px 20px;
  display: block;
`

const Footer = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Status = styled.span<{ grayedOut?: boolean; warning?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: ${FontWeights.Bold};
  text-transform: uppercase;
  opacity: ${({ grayedOut }) => (grayedOut ? '0.6' : '1')};
  ${({ warning }) => warning && 'color: red;'}
`

const SpinnerStyled = styled(Spinner)`
  margin-right: 5px;
`

const TransactionHash = styled.span`
  display: inline-block;
  font-size: 11px;
  color: ${Colors.DarkerGray};
  white-space: nowrap;

  strong {
    color: ${Colors.DarkGray};
    text-transform: uppercase;
  }
`

function ShortenHash({ hash }: { hash: string }) {
  return (
    <>
      <Element>{hash.substring(0, 6)}</Element>
      <Dots>...</Dots>
      <Element>{hash.substring(hash.length - 4)}</Element>
    </>
  )
}

const Element = styled.span``

const Dots = styled.span`
  letter-spacing: -2px;
`
